import { Divider, Tag, Text } from '@oneaudi/unified-web-components';
import { useAtom } from 'jotai';
import { TouchEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { addSortedIntoFilterArray } from '~/app/utils/add-sorted-into-filterarray';
import { arrayWithoutElementAtIndex } from '~/app/utils/array-without-element-at-index';
import { ButtonTabs } from '~/components/button-tabs/button-tabs';
import { Disclaimer } from '~/components/disclaimer/disclaimer';
import { SubFamily } from '~/components/image-selection-tile/components/sub-family';
import { TileInformation } from '~/components/image-selection-tile/components/tile-information';
import {
	FiltersContainer,
	TileContainer,
	TilesContainer,
} from '~/components/image-selection-tile/style/tile-selection-section-style';
import { useConfigurator } from '~/graphql/hooks/use-configurator';
import { FilteredItem } from '~/graphql/types';
import { state } from '~/storage';
import { ImageSelectionTile } from './components/image-selection-tile';

interface TabFilterProps {
	items: FilteredItem[];
	fallbackSVG: JSX.Element;
	isLoading: boolean;
	filters?: string[];
	id: string;
	step?: number;
	totalSteps?: number;
	forcedWayOfFilter?: 'sub' | 'category' | 'no-filter';
	tileHorizontalCenter?: boolean;
}

export const TilesSelection = ({
	items,
	fallbackSVG,
	isLoading,
	filters = [],
	id,
	step,
	totalSteps,
	forcedWayOfFilter,
	tileHorizontalCenter,
}: TabFilterProps) => {
	const [filteredItems, setFilteredItems] = useState<FilteredItem[]>([]);
	const selectedItem = items?.find((item) => item.selected);
	const [activeFilters, setActiveFilters] = useAtom(state.activeFilters);
	const { i18n } = useI18n();
	const { carIdentifier } = useConfigurator();

	const audiExclusiveFilter = i18n('ngc.filter.exclusiveColors');
	const hasAudiExclusiveFilter = filters.includes(audiExclusiveFilter);

	const tilesContainerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if ((!activeFilters || !activeFilters[id] || activeFilters[id].length < 1) && items) {
			setFilteredItems(items);
		}
	}, [
		activeFilters,
		activeFilters[id],
		JSON.stringify(items),
		// Even if the carIdentifier isn't used directly here, it is part of the onSelect function which is part of the items. So the items need to be updated when the carIdentifier changes.
		JSON.stringify(carIdentifier),
	]);

	useEffect(() => {
		// Resets the active filter if the items change, to prevent a filter that is not available anymore to be active
		setActiveFilters((prev) => ({ ...prev, [id]: [] }));
	}, [items.length]);

	useEffect(() => {
		if (items) {
			if (activeFilters && activeFilters[id] && activeFilters[id].length > 0) {
				const additionalItems: FilteredItem[] = [];

				activeFilters[id].forEach((filter) => {
					items
						.filter((item) => {
							if (hasAudiExclusiveFilter) {
								// Filter for Audi Exclusive
								return activeFilters[id].includes(audiExclusiveFilter) === item.audiExclusive;
							}
							return !filter || filter === item.familyName;
						})
						.forEach((itm) => {
							additionalItems.push(itm);
						});
				});

				setFilteredItems(additionalItems);
			}
		}
	}, [
		activeFilters[id],
		filteredItems?.length,
		JSON.stringify(items),
		JSON.stringify(filteredItems),
		id,
		audiExclusiveFilter,
		// The carIdentifier here is needed, because the function needs to be triggered again when the carIdentifier changes so the items can be updated. Each item includes the onSelect function which calls the changeConfiguration function which depends on the updated carIdentifier.
		JSON.stringify(carIdentifier),
	]);

	const handleItemSelection = useCallback((item: FilteredItem) => {
		if (item.readonly) {
			console.warn(
				'The selected item is not changeable. Choosing an equivalent item will (probably) replace it.',
				item,
			);
			return;
		}
		if (item.selected) return;
		item.onSelect();
	}, []);

	const handleFilterClick = useCallback(
		(family: string) => {
			if (!activeFilters) return;
			if (!family) return;

			if (!activeFilters[id]) {
				activeFilters[id] = [];
			}

			// toggle filter
			const idx = activeFilters[id].findIndex((f) => {
				return f.toLowerCase().trim() === family.toLowerCase().trim();
			});

			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.Content,
				eventName: `click to ${activeFilters[id].includes(family) ? 'deselect' : 'select'} filter`,
				label: family,
				category: id,
				elementName: 'tab',
				elementCurrentStep: step ?? 0,
				elementTotalSteps: totalSteps ?? 0,
			});

			if (idx > -1) {
				activeFilters[id] = arrayWithoutElementAtIndex(activeFilters[id], idx);
			} else {
				activeFilters[id] = addSortedIntoFilterArray(activeFilters[id], filters, family);
			}
			setActiveFilters((prev) => ({ ...prev, [id]: activeFilters[id] }));
		},
		[activeFilters, filters, id, setActiveFilters, step, totalSteps],
	);

	const handleTabClick = useCallback(
		(tab: string) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction: EventAction.Content,
				eventName: `selections - tab navi - click on ${tab}`,
				label: activeFilters[id][0],
				category: id,
				elementName: 'tab',
				elementCurrentStep: step ?? 0,
				elementTotalSteps: totalSteps ?? 0,
			});
			setActiveFilters((prev) => ({ ...prev, [id]: [tab] }));
		},
		[id, setActiveFilters],
	);

	const wayOfFilter = useMemo(() => {
		if (forcedWayOfFilter) {
			return forcedWayOfFilter;
		}

		if (items[0]?.subFamilyName) {
			return 'sub';
		}

		if (items[0]?.familyName) {
			return 'category';
		}

		return 'no-filter';
	}, [JSON.stringify(items)]);

	const sortedFilteredItems = useMemo(() => {
		if (!items.length) return null;

		const groupItemsBy = (
			accumulator: Map<string, FilteredItem[]>,
			{ group, item }: { group: string; item: FilteredItem },
		): Map<string, FilteredItem[]> => {
			const groupItems = accumulator.get(group) ?? [];
			groupItems.push(item);
			accumulator.set(group, groupItems);
			return accumulator;
		};

		if (items[0]?.subFamilyName) {
			return filteredItems
				.map((item) => ({ group: item.subFamilyName ?? 'to make ts happy', item }))
				.reduce(groupItemsBy, new Map<string, FilteredItem[]>());
			// group by subFamily
		}

		if (items[0]?.familyName) {
			// group by familyName
			return filteredItems
				.map((item) => ({ group: item.familyName ?? 'to make ts happy', item }))
				.reduce(groupItemsBy, new Map<string, FilteredItem[]>());
		}

		return new Map<string, FilteredItem[]>([['', filteredItems]]);
	}, [filteredItems, items]);

	const [touchStartX, setTouchStartX] = useState(0);
	const [touchStartY, setTouchStartY] = useState(0);
	const [isTouching, setIsTouching] = useState(false);

	const touchStartHandler = (e: TouchEvent<HTMLButtonElement>) => {
		const touch = e.touches[0];
		setTouchStartX(touch.clientX);
		setTouchStartY(touch.clientY);
		setIsTouching(true);
	};

	const touchEndHandler = (e: TouchEvent<HTMLButtonElement>, item: FilteredItem) => {
		if (!isTouching) return;

		const touch = e.changedTouches[0];
		const deltaX = Math.abs(touch.clientX - touchStartX);
		const deltaY = Math.abs(touch.clientY - touchStartY);

		// Define a threshold for movement to distinguish a touch vs a scroll
		const threshold = 20; // pixels

		if (deltaX < threshold && deltaY < threshold) {
			// Trigger your click action only if there's minimal movement
			handleItemSelection(item);
		}

		setIsTouching(false);
	};

	return (
		<>
			{filters && filters.length > 1 && (
				<FiltersContainer>
					{hasAudiExclusiveFilter ? (
						<>
							<ButtonTabs
								setSelectedTab={handleTabClick}
								selectedTab={activeFilters[id][0]}
								tabs={filters.map((filter) => ({ value: filter, label: filter }))}
							/>
							<>
								{activeFilters[id]?.includes(audiExclusiveFilter) &&
									i18n('ngc.disclaimer.exclusiveColor.content') && (
										<Disclaimer
											type="general"
											headline={i18n('ngc.disclaimer.exclusiveColor.headline')}
											content={i18n('ngc.disclaimer.exclusiveColor.content')}
										/>
									)}
							</>
						</>
					) : (
						<>
							<Text variant={{ order: '2', style: 'normal', type: 'copy' }} tag="p">
								{i18n('ngc.filter.by')}
							</Text>

							<div className="filter-tags">
								{filters.map((family) => (
									<Tag
										variant="selectable"
										key={family}
										value={family}
										checked={activeFilters[id]?.includes(family)}
										onChange={() => handleFilterClick(family)}
										label={family}
										size={'sm'}
										// TODO: Currently this pop isn't supported by the changable tag component. Will be part of the next unified release.
										// aria-label={`${i18n('ngc.filter.by')} ${family}`}
									/>
								))}
							</div>
						</>
					)}
				</FiltersContainer>
			)}
			{filteredItems.length ? (
				<>
					<TilesContainer>
						{wayOfFilter === 'sub' ? (
							<div ref={tilesContainerRef}>
								<div className="inner-container-categories" ref={tilesContainerRef}>
									{Array.from(sortedFilteredItems ?? []).map(([subFamilyName, itemsByFamily]) => (
										<SubFamily
											key={subFamilyName}
											name={subFamilyName}
											items={itemsByFamily}
											isLoading={isLoading}
											handleItemSelection={handleItemSelection}
											selectedItem={selectedItem}
											fallbackSVG={fallbackSVG}
										/>
									))}
								</div>
							</div>
						) : (
							<></>
						)}
						{wayOfFilter === 'category' ? (
							<div ref={tilesContainerRef}>
								{Array.from(sortedFilteredItems ?? []).map(([categoryName, tiles]) => {
									return (
										<div className="category-wrapper" key={categoryName}>
											<Text
												key={categoryName}
												className="category-name"
												tag="p"
												variant={{ type: 'copy', order: '2', style: 'normal' }}>
												{categoryName}
											</Text>
											<div className="inner-container">
												{tiles.map((item, index) => {
													// Please do not remove this. This is needed for the screen reader to read the item + price at once.
													const name_id = `${item.key.replace(' ', '')}-tile-information`;
													const price_id = `${name_id}-price`;
													return (
														<TileContainer
															$centerItems={
																!item.disclaimerContent && tileHorizontalCenter
															}
															key={index}>
															<ImageSelectionTile
																key={item.key}
																altText={
																	item.imgUrl
																		? item.name || ''
																		: i18n('ngc.a11y.placeholderImage')
																}
																isLoading={item.loading}
																isSelected={item.selected}
																backgroundImage={item.imgUrl}
																disabled={isLoading || item.loading}
																onClick={() => handleItemSelection(item)}
																onTouchStart={touchStartHandler}
																onTouchEnd={(e) => touchEndHandler(e, item)}
																fallbackSVG={fallbackSVG}
																aria-labelledby={`${name_id} ${price_id}`}
															/>
															<TileInformation
																id={name_id}
																price_id={price_id}
																item={item}
															/>
														</TileContainer>
													);
												})}
											</div>
											<Divider className="divider" />
										</div>
									);
								})}
							</div>
						) : (
							<></>
						)}
						{wayOfFilter === 'no-filter' ? (
							<>
								<div className="inner-container" ref={tilesContainerRef}>
									{filteredItems?.map((item, index) => {
										// Please do not remove this. This is needed for the screen reader to read the item + price at once.
										const name_id = `${item.key.replace(' ', '')}-tile-information`;
										const price_id = `${name_id}-price`;
										return (
											<TileContainer
												$centerItems={!item.disclaimerContent && tileHorizontalCenter}
												key={index}>
												<ImageSelectionTile
													key={item.key}
													altText={
														item.imgUrl
															? item.name || ''
															: i18n('ngc.a11y.placeholderImage')
													}
													isLoading={item.loading}
													isSelected={item.selected}
													backgroundImage={item.imgUrl}
													disabled={isLoading || item.loading}
													onClick={() => handleItemSelection(item)}
													onTouchStart={touchStartHandler}
													onTouchEnd={(e) => touchEndHandler(e, item)}
													fallbackSVG={fallbackSVG}
													aria-labelledby={`${name_id} ${price_id}`}
												/>
												<TileInformation id={name_id} price_id={price_id} item={item} />
											</TileContainer>
										);
									})}
								</div>
							</>
						) : (
							<></>
						)}
					</TilesContainer>
				</>
			) : (
				<Text className="error" variant={{ order: '1', style: 'normal', type: 'copy' }} tag="p">
					{i18n('ngc.filter.notFound')}
				</Text>
			)}
		</>
	);
};
